import { Button } from "primereact/button";
import { Message } from "primereact/message";
import { Tag } from "primereact/tag";
import { Fragment, ReactNode } from "react";
import { useGetBenefitComparisonClusterQuery } from "store/queries/benefitComparison";
import {
  ComparisonEngineTag,
  IComparisonChecksRequest,
  IComparisonExample,
} from "types/BenefitComparison/BenefitComparison";

import { BenefitComparisonChecks } from "./BenefitComparisonChecks";
import {
  aiRemitAccuracyText,
  COMPARISON_BENEFIT_LABELS,
  COMPARISON_ENGINE_LABELS,
  formatComparisonValue,
} from "./benefitComparisonWording";
import { BenefitVerdictPanel } from "./BenefitVerdictPanel";
import { PverifyRequestLink } from "./RemitAccuracyLinks";
import {
  formatConfidence,
  lineLabel,
  READ_FROM_LABELS,
} from "./remitAccuracyWording";

function Tags({
  engineTag,
  aiTag,
  equalsLegacy,
}: {
  engineTag: ComparisonEngineTag;
  aiTag: boolean;
  equalsLegacy: boolean;
}) {
  return (
    <div className="flex gap-2">
      {engineTag && (
        <Tag
          severity="warning"
          value={engineTag === "READ" ? "Engine" : "Engine value"}
        />
      )}
      {aiTag && (
        <Tag
          severity="info"
          value="AI"
          data-testid="benefitComparison.cluster.aiTag"
        />
      )}
      {equalsLegacy && <Tag severity="success" value="= Legacy" />}
    </div>
  );
}

function Row({
  testId,
  location,
  message,
  network,
  value,
  tags,
}: {
  testId: string;
  location: string;
  message: ReactNode;
  network: string;
  value: ReactNode;
  tags: ReactNode;
}) {
  return (
    <tr data-testid={testId}>
      <td className="p-2 white-space-nowrap font-mono text-sm">{location}</td>
      <td className="p-2">{message}</td>
      <td className="p-2">{network}</td>
      <td className="p-2">{value}</td>
      <td className="p-2">{tags}</td>
    </tr>
  );
}

function ExampleResponse({
  example,
  request,
}: {
  example: IComparisonExample;
  request: IComparisonChecksRequest;
}) {
  const value = (amount: number | null) =>
    amount === null ? (
      <span className="text-color-secondary">no value</span>
    ) : (
      formatComparisonValue(amount, request.benefit)
    );
  const linesOf = (serviceIndex: number) =>
    example.lines.filter((line) => line.serviceIndex === serviceIndex);
  const { aiLocation } = example;
  let aiWhere = "nothing (none)";
  if (aiLocation?.type === "SUMMARY")
    aiWhere = `${aiLocation.block} › ${aiLocation.field}`;
  if (aiLocation?.type === "LINE")
    aiWhere = `ServiceDetails[${aiLocation.serviceIndex}] › EligibilityDetails[${aiLocation.lineIndex}]`;

  return (
    <>
      <p className="mt-0" data-testid="benefitComparison.cluster.exampleIntro">
        Example: Pverify Request{" "}
        <PverifyRequestLink requestId={example.pverifyRequestId} /> (
        {example.pverifyResponseId}) · legacy {value(example.legacyValue)} ·
        engine {value(example.engineValue)} · AI {value(example.aiValue)} (
        {formatConfidence(example.aiConfidence)})
      </p>
      <div className="overflow-x-auto">
        <table
          className="w-full border-collapse"
          data-testid="benefitComparison.cluster.lines"
        >
          <thead>
            <tr className="text-left border-bottom-1 surface-border">
              <th className="p-2">Where in the response</th>
              <th className="p-2">Line message</th>
              <th className="p-2">Network</th>
              <th className="p-2">Value</th>
              <th className="p-2" aria-label="Tags" />
            </tr>
          </thead>
          <tbody>
            {example.summaries.map((summary) => (
              <Row
                key={`${summary.block}.${summary.field}`}
                testId={`benefitComparison.cluster.summary.${summary.block}.${summary.field}`}
                location={`${summary.block} › ${summary.field}`}
                message={
                  summary.notes ?? (
                    <span className="text-color-secondary">no notes</span>
                  )
                }
                network="In"
                value={value(summary.value)}
                tags={<Tags {...summary} />}
              />
            ))}
            {example.serviceLines.map((service) => (
              <Fragment key={service.serviceIndex}>
                <tr className="surface-100">
                  <td className="p-2 font-semibold" colSpan={5}>
                    {`${service.serviceName} (ServiceDetails[${service.serviceIndex}])`}
                  </td>
                </tr>
                {linesOf(service.serviceIndex).map((line) => (
                  <Row
                    key={line.lineIndex}
                    testId={`benefitComparison.cluster.line.${line.serviceIndex}.${line.lineIndex}`}
                    location={`EligibilityDetails[${line.lineIndex}]`}
                    message={lineLabel(line)}
                    network={line.planNetworkIndicator ?? "—"}
                    value={value(line.value)}
                    tags={<Tags {...line} />}
                  />
                ))}
              </Fragment>
            ))}
            {example.summaries.length === 0 && example.lines.length === 0 && (
              <tr>
                <td className="p-2" colSpan={5}>
                  No tagged lines in this response.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {!example.aiShown && example.aiValue !== null && (
        <p className="text-sm mt-2 mb-0">
          The AI read {aiWhere}, which is not among the rows above.
        </p>
      )}
      {example.engineValueLines > 1 && (
        <p className="text-sm mt-2 mb-0">
          The engine doesn&apos;t record which of these{" "}
          {example.engineValueLines} lines it read.
        </p>
      )}
      {/* CLE-2820 PR 4 — judge the example right here; the checks below each expand into the same panel. */}
      <div className="border-top-1 surface-border mt-3 pt-3">
        <BenefitVerdictPanel
          pverifyResponseId={example.pverifyResponseId}
          benefit={request.benefit}
          options={example.verdictOptions}
          verdict={example.verdict}
          compact
        />
      </div>
      <p className="text-xs text-color-secondary mb-0">
        Legacy stores no line, so <strong>= Legacy</strong> marks every place
        whose value equals legacy&apos;s — value-equality, not what legacy read.
        {example.hiddenLegacyServiceLines > 0 &&
          ` ${example.hiddenLegacyServiceLines} more service lines carry that value.`}
        {example.outOfNetworkLines > 0 &&
          ` ${example.outOfNetworkLines} out-of-network lines not shown.`}{" "}
        Positions are in the stored response.
      </p>
    </>
  );
}

/** CLE-2820 PR 3 — one disagreement cluster: its counts, a tagged example response, and its checks. */
export function BenefitComparisonCluster({
  request,
  onBack,
}: {
  request: IComparisonChecksRequest;
  onBack: () => void;
}) {
  const { data, isLoading, isError } =
    useGetBenefitComparisonClusterQuery(request);
  const detail = data?.data;
  const cluster = detail?.cluster;

  if (isError)
    return <Message severity="error" text="The cluster could not be loaded." />;

  return (
    <div data-testid="benefitComparison.cluster">
      <Button
        type="button"
        icon="pi pi-angle-left"
        label="Legacy vs Engine vs AI"
        className="p-button-text p-button-sm px-0"
        onClick={onBack}
      />
      <h3 className="mt-3 mb-3">
        {cluster
          ? `${cluster.accountName} · ${cluster.payerName} · ${
              cluster.planName ?? "No plan name"
            } · ${COMPARISON_BENEFIT_LABELS[cluster.benefit]}`
          : "Loading…"}
      </h3>
      {cluster && (
        <div className="flex flex-wrap gap-3 align-items-center mb-3 text-color-secondary">
          <span>
            {cluster.disagreements} of {cluster.checks} checks disagree ·{" "}
            {cluster.onlyEngineDiffers} only engine · {cluster.onlyAiDiffers}{" "}
            only AI · {cluster.onlyLegacyDiffers} only legacy ·{" "}
            {cluster.allDiffer} all differ
          </span>
          <span>
            Engine ({COMPARISON_ENGINE_LABELS[request.engine ?? "QUOTED"]}) read
            from {READ_FROM_LABELS[cluster.readFrom] ?? cluster.readFrom}
          </span>
          <span data-testid="benefitComparison.cluster.aiAccuracy">
            AI against remits: {aiRemitAccuracyText(cluster.aiRemitAccuracy)}
          </span>
          <span data-testid="benefitComparison.cluster.verdicts">
            {detail?.verdicts.recorded ?? 0} reviewed ·{" "}
            {detail?.verdicts.needingReview ?? 0} to review
            {detail?.verdicts.remitConflicts
              ? ` · ⚠️ ${detail.verdicts.remitConflicts} a remit disagrees with`
              : ""}
          </span>
        </div>
      )}
      <Message
        severity="info"
        className="mb-3"
        text="A disagreement is a lead to investigate, not a miss. Remit coverage is mostly ASC, so the AI's remit accuracy may not carry over to office or imaging accounts."
      />
      <div className="border-1 surface-border border-round p-3 mb-4">
        <h4 className="mt-0 mb-3">What the response looks like</h4>
        {detail?.example ? (
          <ExampleResponse example={detail.example} request={request} />
        ) : (
          !isLoading && <p className="m-0">No example response.</p>
        )}
      </div>
      <h4 className="mb-3">Checks that disagree · engine odd one out first</h4>
      <BenefitComparisonChecks request={request} />
    </div>
  );
}
